import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Checkbox,
  Flex,
  FormLabel,
  HStack,
  Icon,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  Center,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Report } from '@finley-technologies/finley-types';

import {
  ReportTypeGQLType,
  useGetReportsLazyQueryGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import { DownloadIconButton } from 'packages/common-frontend/components/IconButton';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  PaginationControl,
  usePaginationLimit,
} from 'packages/common-frontend/components/PaginationControl';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { logOutOnAuthErrors } from 'packages/common-frontend/utils';
import { useAuthToken } from 'packages/logged-out/hooks/useAuthManagement';
import { useReportDownloadUrl } from 'packages/reports/hooks/useReportDownloadUrl';
import { FiCheck } from 'react-icons/fi';

export type ReportsTableProps = {
  creditFacilityId: string;
  refetch?: boolean;
  setRefetch?: React.Dispatch<React.SetStateAction<boolean>>;
};

const previewLabel =
  'Preview reports are generated for internal review, and are not sent to the capital provider.';

const ReportRow = ({
  report,
  reportName,
}: {
  report: {
    id: string;
    reportType: ReportTypeGQLType;
    s3Path: string;
    isPreview: boolean;
    created_at: number;
  };
  reportName?: string;
}) => {
  const { id: reportId, reportType, created_at: createdAt } = report;
  const createdAtStr = commonDate.epochToLocaleDateString(createdAt);
  const _reportName = reportName || _.lowerCase(reportType);
  const toast = useToast();
  const { getReportDownloadUrl, error, loading } = useReportDownloadUrl({
    toast,
    reportId,
    reportName: _reportName,
  });
  if (error) {
    throw error;
  }
  return (
    <Tr>
      <Td>{reportName}</Td>
      <Td>{commonUtils.toTitleCase(reportType)}</Td>
      <Td textAlign="center">
        {report.isPreview ? (
          <Icon aria-label="report is preview" as={FiCheck} />
        ) : (
          '-'
        )}
      </Td>
      <Td>{createdAtStr}</Td>
      <Td textAlign="center">
        <DownloadIconButton
          ariaLabel="download report"
          label="Download report"
          isLoading={loading}
          fontSize="xl"
          onClick={() => {
            Sentry.addBreadcrumb({
              category: 'ui.click',
              message: `Clicked download for ${reportName}`,
              level: Sentry.Severity.Info,
            });
            try {
              getReportDownloadUrl();
            } catch (err: any) {
              if (!toast.isActive(errorToastId)) {
                errorToastConfig.title = `Error getting report download for ${reportName}`;
                toast(errorToastConfig);
              }
              throw err;
            }
          }}
        />
      </Td>
    </Tr>
  );
};

ReportRow.defaultProps = {
  reportName: '',
};

export const ReportsTable: React.FC<ReportsTableProps> = ({
  creditFacilityId,
  refetch = false,
  setRefetch,
}) => {
  const [, , removeAuthToken] = useAuthToken();
  const toast = useToast();
  const history = useHistory();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [includePreviews, setIncludePreviews] = useState<boolean>(false);

  const [reportTypes, setReportTypes] = useState<ReportTypeGQLType[]>(
    _.values(Report.ReportTypeIterable),
  );
  const { pageLimit, setPageLimit, options } = usePaginationLimit(10, [
    10,
    25,
    50,
  ]);
  const [
    getReports,
    { data: reportsData, loading },
  ] = useGetReportsLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onError: ({ graphQLErrors }) => {
      logOutOnAuthErrors(graphQLErrors, removeAuthToken, history);
    },
  });

  const refetchReports = useCallback(
    (cursor?: { after: string } | { before: string }) => {
      getReports({
        variables: {
          ...cursor,
          creditFacilityId,
          limit: pageLimit,
          startDate: startDate
            ? commonDate.transformToStartOfDay(startDate).getTime()
            : null,
          endDate: endDate
            ? commonDate.transformToEndOfDay(endDate).getTime()
            : null,
          reportTypes,
          isPreview: includePreviews ? undefined : false,
        },
      });
    },
    [
      getReports,
      creditFacilityId,
      pageLimit,
      startDate,
      endDate,
      reportTypes,
      includePreviews,
    ],
  );

  useEffect(() => {
    refetchReports();
    if (refetch && setRefetch) {
      setRefetch(false);
    }
  }, [
    getReports,
    setRefetch,
    refetchReports,
    creditFacilityId,
    pageLimit,
    refetch,
    endDate,
    startDate,
    includePreviews,
  ]);

  if (!reportsData || loading) {
    return <Skeleton data-testid="loading-reports-view" height="600px" />;
  }

  const {
    reports: {
      nodes: reports,
      pageInfo: { hasPreviousPage, hasNextPage, startCursor, endCursor },
    },
  } = reportsData;

  return (
    <PaginationControl
      pageLimit={pageLimit}
      setPageLimit={setPageLimit}
      options={options}
      limitLabel="Reports per page"
      hasPreviousPage={hasPreviousPage}
      hasNextPage={hasNextPage}
      onPrevious={() =>
        refetchReports(startCursor ? { before: startCursor } : undefined)
      }
      onNext={() =>
        refetchReports(endCursor ? { after: endCursor } : undefined)
      }
      nextLabel="Older"
      previousLabel="Newer"
      filters={
        <HStack spacing="10">
          <Box>
            <IsolatedMultiSelect
              options={_.map(Report.ReportTypeIterable, (reportType) => ({
                label: commonUtils.toSentenceCase(reportType),
                value: reportType,
              }))}
              initialSelections={reportTypes}
              onApplyOptions={setReportTypes}
              optionsHeader="Report Types"
              titleText={
                reportTypes.length > 0
                  ? `${reportTypes.length} selected report type${
                      reportTypes.length === 1 ? '' : 's'
                    }`
                  : 'No report types selected'
              }
              applyButtonText="Apply filters"
            />
          </Box>
          <Flex alignItems="center">
            <FormLabel mb="0" id="include-previews-label">
              <Center>
                Include Previews{' '}
                <Tooltip hasArrow label={previewLabel}>
                  <InfoOutlineIcon ml="2px" />
                </Tooltip>
              </Center>
            </FormLabel>
            <Checkbox
              aria-labelledby="include-previews-label"
              aria-label="include previews"
              isChecked={includePreviews}
              onChange={() => setIncludePreviews(!includePreviews)}
            />
          </Flex>
          <FilterDatePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        </HStack>
      }
    >
      {reports && !_.isEmpty(reports) ? (
        <Table mt="10px">
          <Thead>
            <Tr>
              <Th width="45%">Name</Th>
              <Th width="15%">Report Type</Th>
              <Th width="10%" textAlign="center">
                <Center>
                  Preview
                  <Tooltip hasArrow label={previewLabel}>
                    <InfoOutlineIcon ml="2px" />
                  </Tooltip>
                </Center>
              </Th>
              <Th width="20%">Created</Th>
              <Th width="15%" textAlign="center">
                Download
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {_.map(reports, (report) => {
              const reportName = _.last(report.s3Path.split('/') as string[]);
              return (
                <Sentry.ErrorBoundary
                  key={report.id}
                  onError={() => {
                    if (!toast.isActive(errorToastId)) {
                      errorToastConfig.title = `Error in showing report ${reportName}`;
                      toast(errorToastConfig);
                    }
                  }}
                >
                  <ReportRow reportName={reportName} report={report} />
                </Sentry.ErrorBoundary>
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Alert status="info" mt="35px">
          <AlertIcon />
          <AlertDescription>No reports to view at this time</AlertDescription>
        </Alert>
      )}
    </PaginationControl>
  );
};
